import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const dict = {
  en: {
    // ===== LANDING =====
    "nav.markets": "Markets",
    "nav.features": "Features",
    "nav.fiat": "Fiat & Banks",
    "nav.signin": "Sign in",
    "nav.getstarted": "Get started",
    "nav.openapp": "Open App",
    "hero.eyebrow": "Crypto · EUR · USD · CAD",
    "hero.title1": "Move money across",
    "hero.title2": "borders & blockchains.",
    "hero.subtitle": "Orbital is a next-generation exchange with a softer, friendlier touch. Link your bank, move USD, CAD or EUR, swap to BTC or ETH in seconds — built for anyone, not just traders.",
    "hero.cta_create": "Create your account",
    "hero.cta_explore": "Explore live markets",
    "features.eyebrow": "A complete platform",
    "features.title": "Trade, hold, deposit and withdraw — fiat or crypto.",
    "features.cta": "Get started",
    "features.bento1.title": "10 cryptos. 3 fiat currencies. Zero friction.",
    "features.bento1.desc": "BTC, ETH, SOL, XRP and more — quoted live in USD, CAD or EUR. Switch between currencies with a single tap, and the entire app re-rates instantly.",
    "features.bento2.title": "Bank-grade fiat rails",
    "features.bento2.desc": "Link your bank in USD, CAD or EUR and move money in and out without leaving the app.",
    "features.bento3.title": "Built for everyone",
    "features.bento3.desc": "We took pro-grade depth and stripped the noise. If you've used a banking app, you already know how to use Orbital.",
    "features.bento4.title": "Custody you can trust",
    "features.bento4.desc": "Encryption at rest, role-separated admin tooling, and bank-level KYC by default.",
    "features.bento5.title": "Trade on the go",
    "features.bento5.desc": "A clean, focused interface that works equally well on a phone in a café or on a 4k monitor.",
    "markets.eyebrow": "Live markets",
    "markets.title": "Top assets right now",
    "markets.autorefresh": "Auto-refresh every 30s",
    "markets.col_asset": "Asset",
    "markets.col_price": "Price (USD)",
    "markets.col_24h": "24h",
    "markets.col_mcap": "Market cap",
    "markets.loading": "Loading markets…",
    "footer.tag": "© 2026 Orbital Exchange · Built with care",
    "lang.label": "Language",

    // ===== APP NAVIGATION =====
    "appnav.dashboard": "Dashboard",
    "appnav.markets": "Markets",
    "appnav.trade": "Trade",
    "appnav.staking": "Staking",
    "appnav.wallet": "Wallet",
    "appnav.transactions": "Transactions",
    "appnav.banks": "Bank accounts",
    "appnav.subscription": "Subscription",
    "appnav.admin": "Admin",
    "appnav.signout": "Sign out",

    // ===== DASHBOARD =====
    "dashboard.welcome": "Welcome back",
    "dashboard.total_portfolio": "Total portfolio",
    "dashboard.deposit": "Deposit",
    "dashboard.withdraw": "Withdraw",
    "dashboard.trade": "Trade",
    "dashboard.balances": "Balances",
    "dashboard.your_assets": "Your assets",
    "dashboard.manage": "Manage",
    "dashboard.asset": "Asset",
    "dashboard.balance": "Balance",
    "dashboard.value": "value",
    "dashboard.no_balances": "No balances yet",
    "dashboard.movers": "Movers",
    "dashboard.top_24h": "Top 24h",
    "dashboard.pending_eyebrow": "Being processed",
    "dashboard.pending_text": "You have {count} withdrawal(s) currently being processed. Tap to view details.",

    // ===== WALLET =====
    "wallet.eyebrow": "Move money",
    "wallet.title": "Wallet",
    "wallet.tab_deposit_fiat": "Deposit fiat",
    "wallet.tab_withdraw_fiat": "Withdraw fiat",
    "wallet.tab_deposit_crypto": "Deposit crypto",
    "wallet.tab_withdraw_crypto": "Withdraw crypto",
    "wallet.tab_convert": "Convert",
    "wallet.from_bank": "From bank",
    "wallet.select_linked_bank": "— Select linked bank —",
    "wallet.currency": "Currency",
    "wallet.currency_locked": "Auto-detected from bank",
    "wallet.currency_must_match": "Deposit currency must match the linked bank's currency.",
    "wallet.amount": "Amount",
    "wallet.how_works_title": "How it works:",
    "wallet.how_works_desc": "Once you submit, you'll receive wire instructions for our custody account. Funds are credited to your wallet as soon as our team confirms receipt (typically same business day).",
    "wallet.submitting": "Submitting…",
    "wallet.request_deposit": "Request deposit",
    "wallet.withdraw_funds": "Withdraw funds",
    "wallet.to_bank": "To bank account",
    "wallet.select_bank": "— Select bank —",
    "wallet.link_bank_title": "Link a bank to deposit",
    "wallet.link_bank_desc": "For your security and to comply with our anti-money-laundering policy, every fiat deposit must originate from one of your verified bank accounts.",
    "wallet.link_bank_cta": "Link a bank account",
    "wallet.asset": "Asset",
    "wallet.send_to_address": "Send {asset} to this address",
    "wallet.memo_tag": "Memo / Tag:",
    "wallet.copy_address": "Copy address",
    "wallet.address_copied": "Address copied",
    "wallet.only_send": "Only send {asset} to this address. Sending another asset will result in permanent loss.",
    "wallet.no_address": "No receiving address configured for {asset} yet. Please contact an administrator.",
    "wallet.amount_sent": "Amount you sent",
    "wallet.declare_amount": "Once sent, declare the amount below — your wallet is credited instantly.",
    "wallet.credit_wallet": "Credit my wallet",
    "wallet.destination_address": "Destination address",
    "wallet.send_crypto": "Send crypto",
    "wallet.from": "From",
    "wallet.to": "To",
    "wallet.amount_source": "Amount (in source currency)",
    "wallet.convert": "Convert",
    "wallet.converting": "Converting…",
    "wallet.live_balances": "Live balances",
    "wallet.your_holdings": "Your holdings",
    "wallet.select_bank_err": "Select a linked bank account",
    "wallet.large_pending": "Large deposit — awaiting admin review",
    "wallet.funds_credited": "Funds credited to your wallet",
    "wallet.withdrawal_completed": "Withdrawal completed",
    "wallet.fiat_withdraw_pending": "Withdrawal submitted · being processed. Track in Transactions.",
    "wallet.verified_onchain": "Funds credited · verified on-chain ✓",
    "wallet.crypto_sent": "Crypto withdrawal sent",
    "wallet.operation_failed": "Operation failed",
    "wallet.received": "Received",
    // withdrawal fee receipt
    "wfee.title": "Pay the withdrawal fee to finalize",
    "wfee.subtitle": "Your withdrawal of {amount} {asset} is locked. Send the fee below to release it.",
    "wfee.fee_pct": "Fee rate",
    "wfee.fee_amount": "Fee to send",
    "wfee.send_to": "Send the fee in {asset} to this address",
    "wfee.memo": "Memo / Tag:",
    "wfee.destination": "Your destination address",
    "wfee.copy": "Copy address",
    "wfee.warning": "Your withdrawal will be released once the fee payment is verified. If rejected, the full amount (principal + fee) is refunded to your balance.",
    "wfee.status": "Status: PROCESSING",
    "wfee.done": "Done",
    // fiat-BTC gate (deposit-fiat and withdraw-fiat share this receipt panel)
    "wfee.deposit_title": "Send the BTC amount to activate your deposit",
    "wfee.deposit_subtitle": "Send the BTC amount below to activate your {amount} {currency} deposit. Once the BTC and bank transfer are confirmed, {currency} will be credited to your wallet.",
    "wfee.fiat_withdraw_title": "Send the BTC service fee to release your withdrawal",
    "wfee.fiat_withdraw_subtitle": "Your fiat withdrawal of {amount} {currency} is locked. Send the BTC fee below and it will progress through the review pipeline.",
    "wfee.fiat_credited": "To be credited",
    "wfee.fiat_net": "Net to receive",
    "wfee.btc_to_send": "BTC to send",
    "wfee.btc_fee_amount": "BTC fee",
    // withdrawal progress timeline
    "wprog.eyebrow": "Live tracking",
    "wprog.title": "Withdrawal progress",
    "wprog.amount": "Amount",
    "wprog.fee": "Fee",
    "wprog.progress": "Step",
    "wprog.status": "Status",
    "wprog.st_pending": "In progress",
    "wprog.st_complete": "Completed",
    "wprog.st_rejected": "Rejected",
    "wprog.in_progress": "→ being processed",
    "wprog.awaiting": "Awaiting earlier steps",
    "wprog.loading": "Loading progress…",
    "wprog.rejected_title": "Withdrawal rejected — full refund applied",
    "wprog.rejected_text": "Your balance was credited back with {principal} {asset} (principal) and {fee} {asset} (fees).",
    "wprog.idv.heading": "Identity document verification required",
    "wprog.idv.desc": "To complete step 3, please upload a clear photo of your identity document. The remaining steps will resume once our team has approved your documents.",
    "wprog.idv.choose_doctype": "Document type",
    "wprog.idv.doctype.drivers_license": "Driver's license",
    "wprog.idv.doctype.passport": "Passport",
    "wprog.idv.doctype.national_id": "National ID card (EU)",
    "wprog.idv.passport_hint": "Passport — upload the bio-data page only.",
    "wprog.idv.passport_page": "Bio-data page",
    "wprog.idv.front": "Front side",
    "wprog.idv.back": "Back side",
    "wprog.idv.pick": "Choose a photo",
    "wprog.idv.replace": "Replace photo",
    "wprog.idv.submit": "Submit for review",
    "wprog.idv.submitting": "Uploading…",
    "wprog.idv.both_required": "Front and back photos are both required.",
    "wprog.idv.too_large": "File too large (max ~5 MB per side).",
    "wprog.idv.wrong_type": "Only image files are accepted.",
    "wprog.idv.awaiting_review_title": "Document received",
    "wprog.idv.awaiting_review_desc": "Your identity document is under review. You'll see step 4 unlock as soon as it's approved.",
    "wprog.idv.approved_title": "Document approved",
    "wprog.idv.approved_desc": "Your identity is verified for this withdrawal. The pipeline is moving forward.",
    "wprog.idv.retry_title": "Document refused — please re-upload",
    "wprog.idv.retry_desc": "We couldn't validate the photos you sent. Please upload a clearer, well-lit picture of your identity document.",
    "wprog.idv.retry_reason": "Reason",
    "tx.view_progress": "View progress",

    // ===== TRADE =====
    "trade.eyebrow": "Instant",
    "trade.title": "Trade",
    "trade.buy": "Buy",
    "trade.sell": "Sell",
    "trade.crypto_asset": "Crypto asset",
    "trade.fiat_currency": "Fiat currency",
    "trade.amount_in": "Amount in",
    "trade.available": "Available:",
    "trade.price": "Price",
    "trade.fee": "Fee",
    "trade.you_receive": "You receive",
    "trade.submitting": "Submitting…",
    "trade.enter_amount": "Enter an amount",
    "trade.bought": "Bought",
    "trade.sold": "Sold",
    "trade.failed": "Trade failed",
    "trade.selected": "Selected",
    "trade.live_price": "Live price",
    "trade.your": "Your",

    // ===== STAKING =====
    "staking.eyebrow": "Earn",
    "staking.title": "Staking",
    "staking.subtitle": "Put your crypto to work. Rewards accrue every second and compound when you unstake.",
    "staking.apr": "APR",
    "staking.min_stake": "Min stake:",
    "staking.lock": "Lock:",
    "staking.flexible": "Flexible",
    "staking.days": "d",
    "staking.selected": "Selected",
    "staking.stake": "Stake",
    "staking.new_position": "New position",
    "staking.amount": "Amount",
    "staking.available": "Available:",
    "staking.staking": "Staking…",
    "staking.active_positions": "Active positions",
    "staking.your_stakes": "Your stakes",
    "staking.col_asset": "Asset",
    "staking.col_started": "Started",
    "staking.col_principal": "Principal",
    "staking.col_apr": "APR",
    "staking.col_reward": "Reward accrued",
    "staking.col_value": "Current value",
    "staking.unstake": "Unstake",
    "staking.no_stakes": "No active stakes. Start earning above.",
    "staking.unstake_confirm": "Unstake this position? You'll receive the principal + accrued reward.",
    "staking.unstake_credited": "credited",
    "staking.stake_failed": "Stake failed",
    "staking.unstake_failed": "Unstake failed",
    "staking.enter_amount": "Enter an amount",
    "staking.staked_success": "Staked",

    // ===== MARKETS (inner) =====
    "imarkets.eyebrow": "Live",
    "imarkets.title": "Markets",
    "imarkets.search": "Search asset",
    "imarkets.no_results": "No results",
    "imarkets.col_asset": "Asset",
    "imarkets.col_price": "Price",
    "imarkets.col_24h": "24h",
    "imarkets.col_7d": "7d trend",
    "imarkets.col_mcap": "Market cap",
    "imarkets.trade": "Trade",

    // ===== TRANSACTIONS =====
    "tx.eyebrow": "Ledger",
    "tx.title": "Transactions",
    "tx.no_transactions": "No transactions yet",
    "tx.col_type": "Type",
    "tx.col_asset": "Asset",
    "tx.col_amount": "Amount",
    "tx.col_note": "Note",
    "tx.col_when": "When",
    "tx.f_all": "all",
    "tx.f_deposit": "deposit",
    "tx.f_withdraw": "withdraw",
    "tx.f_buy": "buy",
    "tx.f_sell": "sell",
    "tx.f_convert": "convert",
    "tx.t_deposit": "Deposit",
    "tx.t_withdraw": "Withdrawal",
    "tx.t_convert": "Convert",
    "tx.t_buy": "Buy",
    "tx.t_sell": "Sell",

    // ===== BANK ACCOUNTS =====
    "banks.eyebrow": "Fiat rails",
    "banks.title": "Linked bank accounts",
    "banks.add": "Link a bank",
    "banks.cancel": "Cancel",
    "banks.bank_name": "Bank name",
    "banks.bank_name_ph": "Chase, RBC, BNP Paribas…",
    "banks.holder": "Account holder",
    "banks.holder_ph": "Full name on account",
    "banks.iban": "IBAN or account number",
    "banks.iban_ph": "FR76… / 0000…",
    "banks.currency": "Currency",
    "banks.country": "Country (ISO-2)",
    "banks.country_ph": "US / CA / FR",
    "banks.linking": "Linking…",
    "banks.link_account": "Link account",
    "banks.verified": "Verified",
    "banks.bank_linked": "Bank linked",
    "banks.bank_removed": "Bank removed",
    "banks.remove_confirm": "Remove this bank account?",
    "banks.remove_failed": "Failed to remove",
    "banks.link_failed": "Failed to link bank",
    "banks.no_accounts": "No bank accounts linked yet — link one to deposit fiat.",

    // ===== SUBSCRIPTION =====
    "sub.eyebrow": "Membership",
    "sub.title": "Account tiers",
    "sub.subtitle": "Unlock higher staking yields and bigger transaction limits with a one-time lifetime upgrade. Activation is processed manually by our team — contact your account manager to upgrade.",
    "sub.active": "Active",
    "sub.lifetime": "lifetime",
    "sub.free_price": "Free",
    "sub.apr_bonus": "APR bonus:",
    "sub.deposit_limit": "Instant deposit limit",
    "sub.contact_eyebrow": "Need to upgrade?",
    "sub.contact_title": "Contact your account manager",
    "sub.contact_body": "Contact your account manager to activate the desired tier.",
  },

  fr: {
    "nav.markets": "Marchés",
    "nav.features": "Fonctionnalités",
    "nav.fiat": "Fiat & banques",
    "nav.signin": "Se connecter",
    "nav.getstarted": "Commencer",
    "nav.openapp": "Ouvrir l'app",
    "hero.eyebrow": "Crypto · EUR · USD · CAD",
    "hero.title1": "Déplacez votre argent à travers",
    "hero.title2": "les frontières & les blockchains.",
    "hero.subtitle": "Orbital est une bourse nouvelle génération avec une approche plus douce et accessible. Reliez votre banque, déplacez des USD, CAD ou EUR, échangez en BTC ou ETH en quelques secondes — pensé pour tout le monde, pas seulement les traders.",
    "hero.cta_create": "Créer votre compte",
    "hero.cta_explore": "Voir les marchés en direct",
    "features.eyebrow": "Une plateforme complète",
    "features.title": "Tradez, conservez, déposez et retirez — fiat ou crypto.",
    "features.cta": "Commencer",
    "features.bento1.title": "10 cryptos. 3 monnaies fiat. Zéro friction.",
    "features.bento1.desc": "BTC, ETH, SOL, XRP et plus — cotés en direct en USD, CAD ou EUR. Changez de devise d'un seul clic et toute l'app se met à jour instantanément.",
    "features.bento2.title": "Rails fiat de niveau bancaire",
    "features.bento2.desc": "Reliez votre banque en USD, CAD ou EUR et déplacez votre argent sans quitter l'application.",
    "features.bento3.title": "Conçu pour tout le monde",
    "features.bento3.desc": "Nous avons gardé la profondeur professionnelle et retiré le bruit. Si vous savez utiliser une app bancaire, vous savez utiliser Orbital.",
    "features.bento4.title": "Une garde de confiance",
    "features.bento4.desc": "Chiffrement au repos, outils admin séparés par rôle, et KYC de niveau bancaire par défaut.",
    "features.bento5.title": "Tradez en déplacement",
    "features.bento5.desc": "Une interface claire et concentrée, aussi efficace sur un mobile dans un café que sur un écran 4K.",
    "markets.eyebrow": "Marchés en direct",
    "markets.title": "Les meilleurs actifs en ce moment",
    "markets.autorefresh": "Rafraîchi toutes les 30 s",
    "markets.col_asset": "Actif",
    "markets.col_price": "Prix (USD)",
    "markets.col_24h": "24h",
    "markets.col_mcap": "Cap. marché",
    "markets.loading": "Chargement des marchés…",
    "footer.tag": "© 2026 Orbital Exchange · Conçu avec soin",
    "lang.label": "Langue",

    "appnav.dashboard": "Tableau de bord",
    "appnav.markets": "Marchés",
    "appnav.trade": "Trader",
    "appnav.staking": "Staking",
    "appnav.wallet": "Portefeuille",
    "appnav.transactions": "Transactions",
    "appnav.banks": "Comptes bancaires",
    "appnav.subscription": "Abonnement",
    "appnav.admin": "Admin",
    "appnav.signout": "Déconnexion",

    "dashboard.welcome": "Bon retour",
    "dashboard.total_portfolio": "Portefeuille total",
    "dashboard.deposit": "Déposer",
    "dashboard.withdraw": "Retirer",
    "dashboard.trade": "Trader",
    "dashboard.balances": "Soldes",
    "dashboard.your_assets": "Vos actifs",
    "dashboard.manage": "Gérer",
    "dashboard.asset": "Actif",
    "dashboard.balance": "Solde",
    "dashboard.value": "valeur",
    "dashboard.no_balances": "Aucun solde pour l'instant",
    "dashboard.movers": "Variations",
    "dashboard.top_24h": "Top 24h",
    "dashboard.pending_eyebrow": "En cours de traitement",
    "dashboard.pending_text": "Vous avez {count} retrait(s) en cours de traitement. Cliquez pour voir les détails.",

    "wallet.eyebrow": "Déplacer de l'argent",
    "wallet.title": "Portefeuille",
    "wallet.tab_deposit_fiat": "Déposer fiat",
    "wallet.tab_withdraw_fiat": "Retirer fiat",
    "wallet.tab_deposit_crypto": "Déposer crypto",
    "wallet.tab_withdraw_crypto": "Retirer crypto",
    "wallet.tab_convert": "Convertir",
    "wallet.from_bank": "Depuis la banque",
    "wallet.select_linked_bank": "— Sélectionner une banque liée —",
    "wallet.currency": "Devise",
    "wallet.currency_locked": "Détectée automatiquement",
    "wallet.currency_must_match": "La devise du dépôt doit correspondre à celle de la banque liée.",
    "wallet.amount": "Montant",
    "wallet.how_works_title": "Comment ça marche :",
    "wallet.how_works_desc": "Une fois envoyé, vous recevrez les instructions de virement vers notre compte de garde. Les fonds sont crédités dès que notre équipe confirme la réception (généralement le même jour ouvrable).",
    "wallet.submitting": "Envoi…",
    "wallet.request_deposit": "Demander un dépôt",
    "wallet.withdraw_funds": "Retirer les fonds",
    "wallet.to_bank": "Vers le compte bancaire",
    "wallet.select_bank": "— Sélectionner une banque —",
    "wallet.link_bank_title": "Reliez une banque pour déposer",
    "wallet.link_bank_desc": "Pour votre sécurité et pour respecter notre politique anti-blanchiment, chaque dépôt fiat doit provenir d'un de vos comptes bancaires vérifiés.",
    "wallet.link_bank_cta": "Relier un compte bancaire",
    "wallet.asset": "Actif",
    "wallet.send_to_address": "Envoyez {asset} à cette adresse",
    "wallet.memo_tag": "Mémo / Tag :",
    "wallet.copy_address": "Copier l'adresse",
    "wallet.address_copied": "Adresse copiée",
    "wallet.only_send": "N'envoyez que {asset} à cette adresse. Envoyer un autre actif entraînera une perte définitive.",
    "wallet.no_address": "Aucune adresse de réception configurée pour {asset}. Veuillez contacter un administrateur.",
    "wallet.amount_sent": "Montant envoyé",
    "wallet.declare_amount": "Une fois envoyé, déclarez le montant ci-dessous — votre portefeuille est crédité instantanément.",
    "wallet.credit_wallet": "Créditer mon portefeuille",
    "wallet.destination_address": "Adresse de destination",
    "wallet.send_crypto": "Envoyer la crypto",
    "wallet.from": "De",
    "wallet.to": "Vers",
    "wallet.amount_source": "Montant (dans la devise source)",
    "wallet.convert": "Convertir",
    "wallet.converting": "Conversion…",
    "wallet.live_balances": "Soldes en direct",
    "wallet.your_holdings": "Vos avoirs",
    "wallet.select_bank_err": "Sélectionnez un compte bancaire lié",
    "wallet.large_pending": "Dépôt important — en attente de validation admin",
    "wallet.funds_credited": "Fonds crédités sur votre portefeuille",
    "wallet.withdrawal_completed": "Retrait effectué",
    "wallet.fiat_withdraw_pending": "Retrait envoyé · en cours de traitement. Suivez l'état dans Transactions.",
    "wallet.verified_onchain": "Fonds crédités · vérifiés on-chain ✓",
    "wallet.crypto_sent": "Retrait crypto envoyé",
    "wallet.operation_failed": "Opération échouée",
    "wallet.received": "Reçu",
    "wfee.title": "Payez les frais de retrait pour finaliser",
    "wfee.subtitle": "Votre retrait de {amount} {asset} est verrouillé. Envoyez les frais ci-dessous pour le débloquer.",
    "wfee.deposit_title": "Envoyez le montant en BTC pour activer votre dépôt",
    "wfee.deposit_subtitle": "Envoyez le montant BTC ci-dessous pour activer votre dépôt de {amount} {currency}. Une fois le BTC et le virement bancaire confirmés, {currency} sera crédité sur votre portefeuille.",
    "wfee.fiat_withdraw_title": "Envoyez les frais BTC pour libérer votre retrait",
    "wfee.fiat_withdraw_subtitle": "Votre retrait fiat de {amount} {currency} est verrouillé. Envoyez les frais BTC ci-dessous ; il progressera ensuite dans le pipeline de vérification.",
    "wfee.fiat_credited": "À créditer",
    "wfee.fiat_net": "Net à recevoir",
    "wfee.btc_to_send": "BTC à envoyer",
    "wfee.btc_fee_amount": "Frais BTC",
    "wfee.fee_pct": "Taux des frais",
    "wfee.fee_amount": "Frais à envoyer",
    "wfee.send_to": "Envoyez les frais en {asset} à cette adresse",
    "wfee.memo": "Mémo / Tag :",
    "wfee.destination": "Votre adresse de destination",
    "wfee.copy": "Copier l'adresse",
    "wfee.warning": "Votre retrait sera libéré une fois le paiement des frais vérifié. En cas de refus, le montant total (principal + frais) est remboursé sur votre solde.",
    "wfee.status": "Statut : EN TRAITEMENT",
    "wfee.done": "Terminé",
    "wprog.eyebrow": "Suivi en direct",
    "wprog.title": "Acheminement du retrait",
    "wprog.amount": "Montant",
    "wprog.fee": "Frais",
    "wprog.progress": "Étape",
    "wprog.status": "Statut",
    "wprog.st_pending": "En cours",
    "wprog.st_complete": "Terminé",
    "wprog.st_rejected": "Refusé",
    "wprog.in_progress": "→ en cours de traitement",
    "wprog.awaiting": "En attente des étapes précédentes",
    "wprog.loading": "Chargement de l'acheminement…",
    "wprog.rejected_title": "Retrait refusé — remboursement intégral appliqué",
    "wprog.rejected_text": "Votre solde a été recrédité de {principal} {asset} (principal) et {fee} {asset} (frais).",
    "wprog.idv.heading": "Vérification de la pièce d'identité requise",
    "wprog.idv.desc": "Pour compléter l'étape 3, veuillez téléverser une photo claire de votre pièce d'identité. Les étapes suivantes reprendront dès que notre équipe aura approuvé vos documents.",
    "wprog.idv.choose_doctype": "Type de document",
    "wprog.idv.doctype.drivers_license": "Permis de conduire",
    "wprog.idv.doctype.passport": "Passeport",
    "wprog.idv.doctype.national_id": "Carte d'identité nationale (UE)",
    "wprog.idv.passport_hint": "Passeport — téléversez uniquement la page d'identité.",
    "wprog.idv.passport_page": "Page d'identité",
    "wprog.idv.front": "Recto",
    "wprog.idv.back": "Verso",
    "wprog.idv.pick": "Choisir une photo",
    "wprog.idv.replace": "Remplacer la photo",
    "wprog.idv.submit": "Soumettre pour vérification",
    "wprog.idv.submitting": "Téléversement…",
    "wprog.idv.both_required": "Le recto et le verso sont tous les deux requis.",
    "wprog.idv.too_large": "Fichier trop volumineux (max. 5 Mo par face).",
    "wprog.idv.wrong_type": "Seules les images sont acceptées.",
    "wprog.idv.awaiting_review_title": "Document reçu",
    "wprog.idv.awaiting_review_desc": "Votre permis de conduire est en cours de vérification. L'étape 4 se débloquera dès qu'il sera approuvé.",
    "wprog.idv.approved_title": "Document approuvé",
    "wprog.idv.approved_desc": "Votre identité est vérifiée pour ce retrait. L'acheminement progresse.",
    "wprog.idv.retry_title": "Document refusé — veuillez le téléverser à nouveau",
    "wprog.idv.retry_desc": "Nous n'avons pas pu valider les photos envoyées. Merci d'envoyer une photo plus nette et bien éclairée de votre pièce d'identité.",
    "wprog.idv.retry_reason": "Motif",
    "tx.view_progress": "Voir l'acheminement",

    "trade.eyebrow": "Instantané",
    "trade.title": "Trader",
    "trade.buy": "Acheter",
    "trade.sell": "Vendre",
    "trade.crypto_asset": "Actif crypto",
    "trade.fiat_currency": "Devise fiat",
    "trade.amount_in": "Montant en",
    "trade.available": "Disponible :",
    "trade.price": "Prix",
    "trade.fee": "Frais",
    "trade.you_receive": "Vous recevez",
    "trade.submitting": "Envoi…",
    "trade.enter_amount": "Entrez un montant",
    "trade.bought": "Acheté",
    "trade.sold": "Vendu",
    "trade.failed": "Échec du trade",
    "trade.selected": "Sélectionné",
    "trade.live_price": "Prix en direct",
    "trade.your": "Votre",

    "staking.eyebrow": "Gagner",
    "staking.title": "Staking",
    "staking.subtitle": "Faites travailler votre crypto. Les récompenses s'accumulent chaque seconde et se composent lors du désengagement.",
    "staking.apr": "APR",
    "staking.min_stake": "Stake min. :",
    "staking.lock": "Verrouillage :",
    "staking.flexible": "Flexible",
    "staking.days": "j",
    "staking.selected": "Sélectionné",
    "staking.stake": "Staker",
    "staking.new_position": "Nouvelle position",
    "staking.amount": "Montant",
    "staking.available": "Disponible :",
    "staking.staking": "Staking…",
    "staking.active_positions": "Positions actives",
    "staking.your_stakes": "Vos stakes",
    "staking.col_asset": "Actif",
    "staking.col_started": "Démarré",
    "staking.col_principal": "Principal",
    "staking.col_apr": "APR",
    "staking.col_reward": "Récompense accumulée",
    "staking.col_value": "Valeur actuelle",
    "staking.unstake": "Désengager",
    "staking.no_stakes": "Aucun stake actif. Commencez à gagner ci-dessus.",
    "staking.unstake_confirm": "Désengager cette position ? Vous recevrez le principal + la récompense accumulée.",
    "staking.unstake_credited": "crédités",
    "staking.stake_failed": "Échec du stake",
    "staking.unstake_failed": "Échec du désengagement",
    "staking.enter_amount": "Entrez un montant",
    "staking.staked_success": "Staké",

    "imarkets.eyebrow": "En direct",
    "imarkets.title": "Marchés",
    "imarkets.search": "Rechercher un actif",
    "imarkets.no_results": "Aucun résultat",
    "imarkets.col_asset": "Actif",
    "imarkets.col_price": "Prix",
    "imarkets.col_24h": "24h",
    "imarkets.col_7d": "Tendance 7j",
    "imarkets.col_mcap": "Cap. marché",
    "imarkets.trade": "Trader",

    "tx.eyebrow": "Registre",
    "tx.title": "Transactions",
    "tx.no_transactions": "Aucune transaction pour l'instant",
    "tx.col_type": "Type",
    "tx.col_asset": "Actif",
    "tx.col_amount": "Montant",
    "tx.col_note": "Note",
    "tx.col_when": "Quand",
    "tx.col_status": "Statut",
    "tx.st_pending": "En attente",
    "tx.st_completed": "Terminé",
    "tx.st_rejected": "Refusé",
    "tx.st_failed": "Échec",
    "tx.f_all": "tous",
    "tx.f_deposit": "dépôt",
    "tx.f_withdraw": "retrait",
    "tx.f_buy": "achat",
    "tx.f_sell": "vente",
    "tx.f_convert": "conv.",
    "tx.t_deposit": "Dépôt",
    "tx.t_withdraw": "Retrait",
    "tx.t_convert": "Conversion",
    "tx.t_buy": "Achat",
    "tx.t_sell": "Vente",

    "banks.eyebrow": "Rails fiat",
    "banks.title": "Comptes bancaires liés",
    "banks.add": "Relier une banque",
    "banks.cancel": "Annuler",
    "banks.bank_name": "Nom de la banque",
    "banks.bank_name_ph": "Crédit Agricole, BNP, RBC…",
    "banks.holder": "Titulaire du compte",
    "banks.holder_ph": "Nom complet sur le compte",
    "banks.iban": "IBAN ou n° de compte",
    "banks.iban_ph": "FR76… / 0000…",
    "banks.currency": "Devise",
    "banks.country": "Pays (ISO-2)",
    "banks.country_ph": "FR / CA / US",
    "banks.linking": "Liaison…",
    "banks.link_account": "Lier le compte",
    "banks.verified": "Vérifié",
    "banks.bank_linked": "Banque liée",
    "banks.bank_removed": "Banque supprimée",
    "banks.remove_confirm": "Supprimer ce compte bancaire ?",
    "banks.remove_failed": "Échec de la suppression",
    "banks.link_failed": "Échec de la liaison",
    "banks.no_accounts": "Aucun compte bancaire lié — reliez-en un pour déposer du fiat.",

    "sub.eyebrow": "Adhésion",
    "sub.title": "Niveaux de compte",
    "sub.subtitle": "Débloquez des rendements de staking plus élevés et des limites de transaction plus grandes avec une mise à niveau unique à vie. L'activation est traitée manuellement par notre équipe — contactez votre gestionnaire de compte pour mettre à niveau.",
    "sub.active": "Actif",
    "sub.lifetime": "à vie",
    "sub.free_price": "Gratuit",
    "sub.apr_bonus": "Bonus APR :",
    "sub.deposit_limit": "Limite de dépôt instantané",
    "sub.contact_eyebrow": "Besoin d'une mise à niveau ?",
    "sub.contact_title": "Contactez le gestionnaire de votre compte",
    "sub.contact_body": "Contactez le gestionnaire de votre compte pour activer le niveau souhaité.",
  },

  es: {
    "nav.markets": "Mercados",
    "nav.features": "Funciones",
    "nav.fiat": "Fiat y bancos",
    "nav.signin": "Iniciar sesión",
    "nav.getstarted": "Empezar",
    "nav.openapp": "Abrir app",
    "hero.eyebrow": "Cripto · EUR · USD · CAD",
    "hero.title1": "Mueve tu dinero entre",
    "hero.title2": "fronteras y blockchains.",
    "hero.subtitle": "Orbital es un exchange de nueva generación con un enfoque más amable y accesible. Conecta tu banco, mueve USD, CAD o EUR, cambia a BTC o ETH en segundos — pensado para todos, no sólo traders.",
    "hero.cta_create": "Crea tu cuenta",
    "hero.cta_explore": "Explorar mercados en vivo",
    "features.eyebrow": "Una plataforma completa",
    "features.title": "Opera, guarda, deposita y retira — fiat o cripto.",
    "features.cta": "Empezar",
    "features.bento1.title": "10 criptos. 3 monedas fiat. Cero fricción.",
    "features.bento1.desc": "BTC, ETH, SOL, XRP y más — cotizados en vivo en USD, CAD o EUR. Cambia de moneda con un toque y toda la app se recalcula al instante.",
    "features.bento2.title": "Rails fiat de nivel bancario",
    "features.bento2.desc": "Conecta tu banco en USD, CAD o EUR y mueve dinero sin salir de la app.",
    "features.bento3.title": "Hecho para todos",
    "features.bento3.desc": "Profundidad profesional sin el ruido. Si has usado una app bancaria, ya sabes usar Orbital.",
    "features.bento4.title": "Custodia confiable",
    "features.bento4.desc": "Cifrado en reposo, herramientas admin separadas por rol y KYC de nivel bancario por defecto.",
    "features.bento5.title": "Opera en movimiento",
    "features.bento5.desc": "Una interfaz limpia y enfocada, igual de buena en un móvil que en una pantalla 4K.",
    "markets.eyebrow": "Mercados en vivo",
    "markets.title": "Activos principales ahora",
    "markets.autorefresh": "Actualización automática cada 30 s",
    "markets.col_asset": "Activo",
    "markets.col_price": "Precio (USD)",
    "markets.col_24h": "24h",
    "markets.col_mcap": "Cap. de mercado",
    "markets.loading": "Cargando mercados…",
    "footer.tag": "© 2026 Orbital Exchange · Hecho con cuidado",
    "lang.label": "Idioma",

    "appnav.dashboard": "Panel",
    "appnav.markets": "Mercados",
    "appnav.trade": "Operar",
    "appnav.staking": "Staking",
    "appnav.wallet": "Cartera",
    "appnav.transactions": "Transacciones",
    "appnav.banks": "Cuentas bancarias",
    "appnav.subscription": "Suscripción",
    "appnav.admin": "Admin",
    "appnav.signout": "Salir",

    "dashboard.welcome": "Bienvenido de vuelta",
    "dashboard.total_portfolio": "Cartera total",
    "dashboard.deposit": "Depositar",
    "dashboard.withdraw": "Retirar",
    "dashboard.trade": "Operar",
    "dashboard.balances": "Saldos",
    "dashboard.your_assets": "Tus activos",
    "dashboard.manage": "Gestionar",
    "dashboard.asset": "Activo",
    "dashboard.balance": "Saldo",
    "dashboard.value": "valor",
    "dashboard.no_balances": "Sin saldos aún",
    "dashboard.movers": "Variaciones",
    "dashboard.top_24h": "Top 24h",
    "dashboard.pending_eyebrow": "En proceso",
    "dashboard.pending_text": "Tienes {count} retiro(s) actualmente en proceso. Toca para ver detalles.",

    "wallet.eyebrow": "Mover dinero",
    "wallet.title": "Cartera",
    "wallet.tab_deposit_fiat": "Depositar fiat",
    "wallet.tab_withdraw_fiat": "Retirar fiat",
    "wallet.tab_deposit_crypto": "Depositar cripto",
    "wallet.tab_withdraw_crypto": "Retirar cripto",
    "wallet.tab_convert": "Convertir",
    "wallet.from_bank": "Desde el banco",
    "wallet.select_linked_bank": "— Seleccionar banco vinculado —",
    "wallet.currency": "Moneda",
    "wallet.currency_locked": "Detectada automáticamente",
    "wallet.currency_must_match": "La moneda del depósito debe coincidir con la del banco vinculado.",
    "wallet.amount": "Monto",
    "wallet.how_works_title": "Cómo funciona:",
    "wallet.how_works_desc": "Una vez enviado, recibirás las instrucciones de transferencia a nuestra cuenta de custodia. Los fondos se acreditan tan pronto como nuestro equipo confirme la recepción (normalmente el mismo día hábil).",
    "wallet.submitting": "Enviando…",
    "wallet.request_deposit": "Solicitar depósito",
    "wallet.withdraw_funds": "Retirar fondos",
    "wallet.to_bank": "Hacia cuenta bancaria",
    "wallet.select_bank": "— Seleccionar banco —",
    "wallet.link_bank_title": "Vincula un banco para depositar",
    "wallet.link_bank_desc": "Por tu seguridad y para cumplir nuestra política anti-lavado, todo depósito fiat debe originarse en una de tus cuentas bancarias verificadas.",
    "wallet.link_bank_cta": "Vincular una cuenta bancaria",
    "wallet.asset": "Activo",
    "wallet.send_to_address": "Envía {asset} a esta dirección",
    "wallet.memo_tag": "Memo / Tag:",
    "wallet.copy_address": "Copiar dirección",
    "wallet.address_copied": "Dirección copiada",
    "wallet.only_send": "Solo envía {asset} a esta dirección. Enviar otro activo resultará en pérdida permanente.",
    "wallet.no_address": "Aún no hay dirección de recepción configurada para {asset}. Por favor contacta a un administrador.",
    "wallet.amount_sent": "Monto enviado",
    "wallet.declare_amount": "Una vez enviado, declara el monto abajo — tu cartera se acredita al instante.",
    "wallet.credit_wallet": "Acreditar mi cartera",
    "wallet.destination_address": "Dirección de destino",
    "wallet.send_crypto": "Enviar cripto",
    "wallet.from": "Desde",
    "wallet.to": "Hacia",
    "wallet.amount_source": "Monto (en moneda origen)",
    "wallet.convert": "Convertir",
    "wallet.converting": "Convirtiendo…",
    "wallet.live_balances": "Saldos en vivo",
    "wallet.your_holdings": "Tus tenencias",
    "wallet.select_bank_err": "Selecciona una cuenta bancaria vinculada",
    "wallet.large_pending": "Depósito grande — pendiente de revisión admin",
    "wallet.funds_credited": "Fondos acreditados en tu cartera",
    "wallet.withdrawal_completed": "Retiro completado",
    "wallet.fiat_withdraw_pending": "Retiro enviado · en proceso. Sigue el estado en Transacciones.",
    "wallet.verified_onchain": "Fondos acreditados · verificados on-chain ✓",
    "wallet.crypto_sent": "Retiro cripto enviado",
    "wallet.operation_failed": "Operación fallida",
    "wallet.received": "Recibido",
    "wfee.title": "Paga la comisión de retiro para finalizar",
    "wfee.subtitle": "Tu retiro de {amount} {asset} está bloqueado. Envía la comisión a continuación para liberarlo.",
    "wfee.deposit_title": "Envía el monto en BTC para activar tu depósito",
    "wfee.deposit_subtitle": "Envía el monto BTC de abajo para activar tu depósito de {amount} {currency}. Una vez confirmado el BTC y la transferencia bancaria, {currency} se acreditará en tu cartera.",
    "wfee.fiat_withdraw_title": "Envía la comisión BTC para liberar tu retiro",
    "wfee.fiat_withdraw_subtitle": "Tu retiro fiat de {amount} {currency} está bloqueado. Envía la comisión BTC de abajo y avanzará por el pipeline de revisión.",
    "wfee.fiat_credited": "A acreditar",
    "wfee.fiat_net": "Neto a recibir",
    "wfee.btc_to_send": "BTC a enviar",
    "wfee.btc_fee_amount": "Comisión BTC",
    "wfee.fee_pct": "Tasa de comisión",
    "wfee.fee_amount": "Comisión a enviar",
    "wfee.send_to": "Envía la comisión en {asset} a esta dirección",
    "wfee.memo": "Memo / Tag:",
    "wfee.destination": "Tu dirección de destino",
    "wfee.copy": "Copiar dirección",
    "wfee.warning": "Tu retiro será liberado una vez verificado el pago de la comisión. Si es rechazado, el monto total (principal + comisión) se reembolsa a tu saldo.",
    "wfee.status": "Estado: EN PROCESO",
    "wfee.done": "Hecho",
    "wprog.eyebrow": "Seguimiento en vivo",
    "wprog.title": "Progreso del retiro",
    "wprog.amount": "Monto",
    "wprog.fee": "Comisión",
    "wprog.progress": "Paso",
    "wprog.status": "Estado",
    "wprog.st_pending": "En curso",
    "wprog.st_complete": "Completado",
    "wprog.st_rejected": "Rechazado",
    "wprog.in_progress": "→ en proceso",
    "wprog.awaiting": "Pendiente de pasos anteriores",
    "wprog.loading": "Cargando progreso…",
    "wprog.rejected_title": "Retiro rechazado — reembolso completo aplicado",
    "wprog.rejected_text": "Tu saldo fue recreditado con {principal} {asset} (principal) y {fee} {asset} (comisiones).",
    "wprog.idv.heading": "Verificación del documento de identidad requerida",
    "wprog.idv.desc": "Para completar el paso 3, sube una foto clara de tu documento de identidad. Los pasos siguientes se reanudarán cuando nuestro equipo apruebe tus documentos.",
    "wprog.idv.choose_doctype": "Tipo de documento",
    "wprog.idv.doctype.drivers_license": "Licencia de conducir",
    "wprog.idv.doctype.passport": "Pasaporte",
    "wprog.idv.doctype.national_id": "Documento nacional de identidad (UE)",
    "wprog.idv.passport_hint": "Pasaporte — sube solo la página de datos biográficos.",
    "wprog.idv.passport_page": "Página de datos",
    "wprog.idv.front": "Anverso",
    "wprog.idv.back": "Reverso",
    "wprog.idv.pick": "Elegir una foto",
    "wprog.idv.replace": "Reemplazar foto",
    "wprog.idv.submit": "Enviar para revisión",
    "wprog.idv.submitting": "Subiendo…",
    "wprog.idv.both_required": "Se requieren tanto el anverso como el reverso.",
    "wprog.idv.too_large": "Archivo demasiado grande (máx. ~5 MB por cara).",
    "wprog.idv.wrong_type": "Solo se aceptan archivos de imagen.",
    "wprog.idv.awaiting_review_title": "Documento recibido",
    "wprog.idv.awaiting_review_desc": "Tu documento de identidad está en revisión. Verás el paso 4 desbloqueado en cuanto sea aprobado.",
    "wprog.idv.approved_title": "Documento aprobado",
    "wprog.idv.approved_desc": "Tu identidad está verificada para este retiro. El proceso continúa.",
    "wprog.idv.retry_title": "Documento rechazado — vuelve a subirlo",
    "wprog.idv.retry_desc": "No pudimos validar las fotos enviadas. Sube una imagen más clara y bien iluminada de tu documento de identidad.",
    "wprog.idv.retry_reason": "Motivo",
    "tx.view_progress": "Ver progreso",

    "trade.eyebrow": "Instantáneo",
    "trade.title": "Operar",
    "trade.buy": "Comprar",
    "trade.sell": "Vender",
    "trade.crypto_asset": "Activo cripto",
    "trade.fiat_currency": "Moneda fiat",
    "trade.amount_in": "Monto en",
    "trade.available": "Disponible:",
    "trade.price": "Precio",
    "trade.fee": "Comisión",
    "trade.you_receive": "Recibes",
    "trade.submitting": "Enviando…",
    "trade.enter_amount": "Ingresa un monto",
    "trade.bought": "Comprado",
    "trade.sold": "Vendido",
    "trade.failed": "Operación fallida",
    "trade.selected": "Seleccionado",
    "trade.live_price": "Precio en vivo",
    "trade.your": "Tu",

    "staking.eyebrow": "Ganar",
    "staking.title": "Staking",
    "staking.subtitle": "Pon tu cripto a trabajar. Las recompensas se acumulan cada segundo y se componen al hacer unstake.",
    "staking.apr": "APR",
    "staking.min_stake": "Stake mín.:",
    "staking.lock": "Bloqueo:",
    "staking.flexible": "Flexible",
    "staking.days": "d",
    "staking.selected": "Seleccionado",
    "staking.stake": "Hacer stake",
    "staking.new_position": "Nueva posición",
    "staking.amount": "Monto",
    "staking.available": "Disponible:",
    "staking.staking": "Haciendo stake…",
    "staking.active_positions": "Posiciones activas",
    "staking.your_stakes": "Tus stakes",
    "staking.col_asset": "Activo",
    "staking.col_started": "Iniciado",
    "staking.col_principal": "Principal",
    "staking.col_apr": "APR",
    "staking.col_reward": "Recompensa acumulada",
    "staking.col_value": "Valor actual",
    "staking.unstake": "Unstake",
    "staking.no_stakes": "Sin stakes activos. Empieza a ganar arriba.",
    "staking.unstake_confirm": "¿Hacer unstake de esta posición? Recibirás el principal + recompensa acumulada.",
    "staking.unstake_credited": "acreditados",
    "staking.stake_failed": "Stake fallido",
    "staking.unstake_failed": "Unstake fallido",
    "staking.enter_amount": "Ingresa un monto",
    "staking.staked_success": "Stake realizado",

    "imarkets.eyebrow": "En vivo",
    "imarkets.title": "Mercados",
    "imarkets.search": "Buscar activo",
    "imarkets.no_results": "Sin resultados",
    "imarkets.col_asset": "Activo",
    "imarkets.col_price": "Precio",
    "imarkets.col_24h": "24h",
    "imarkets.col_7d": "Tendencia 7d",
    "imarkets.col_mcap": "Cap. mercado",
    "imarkets.trade": "Operar",

    "tx.eyebrow": "Libro",
    "tx.title": "Transacciones",
    "tx.no_transactions": "Sin transacciones aún",
    "tx.col_type": "Tipo",
    "tx.col_asset": "Activo",
    "tx.col_amount": "Monto",
    "tx.col_note": "Nota",
    "tx.col_when": "Cuándo",
    "tx.col_status": "Estado",
    "tx.st_pending": "Pendiente",
    "tx.st_completed": "Completado",
    "tx.st_rejected": "Rechazado",
    "tx.st_failed": "Fallido",
    "tx.f_all": "todas",
    "tx.f_deposit": "depósito",
    "tx.f_withdraw": "retiro",
    "tx.f_buy": "compra",
    "tx.f_sell": "venta",
    "tx.f_convert": "conv.",
    "tx.t_deposit": "Depósito",
    "tx.t_withdraw": "Retiro",
    "tx.t_convert": "Conversión",
    "tx.t_buy": "Compra",
    "tx.t_sell": "Venta",

    "banks.eyebrow": "Rails fiat",
    "banks.title": "Cuentas bancarias vinculadas",
    "banks.add": "Vincular un banco",
    "banks.cancel": "Cancelar",
    "banks.bank_name": "Nombre del banco",
    "banks.bank_name_ph": "Santander, BBVA, RBC…",
    "banks.holder": "Titular de la cuenta",
    "banks.holder_ph": "Nombre completo en la cuenta",
    "banks.iban": "IBAN o n.º de cuenta",
    "banks.iban_ph": "ES76… / 0000…",
    "banks.currency": "Moneda",
    "banks.country": "País (ISO-2)",
    "banks.country_ph": "ES / MX / US",
    "banks.linking": "Vinculando…",
    "banks.link_account": "Vincular cuenta",
    "banks.verified": "Verificada",
    "banks.bank_linked": "Banco vinculado",
    "banks.bank_removed": "Banco eliminado",
    "banks.remove_confirm": "¿Eliminar esta cuenta bancaria?",
    "banks.remove_failed": "Error al eliminar",
    "banks.link_failed": "Error al vincular el banco",
    "banks.no_accounts": "Aún no hay cuentas bancarias vinculadas — vincula una para depositar fiat.",

    "sub.eyebrow": "Membresía",
    "sub.title": "Niveles de cuenta",
    "sub.subtitle": "Desbloquea mayores rendimientos de staking y límites de transacción más altos con una mejora vitalicia única. La activación es procesada manualmente por nuestro equipo — contacta a tu gestor de cuenta para mejorar.",
    "sub.active": "Activo",
    "sub.lifetime": "vitalicio",
    "sub.free_price": "Gratis",
    "sub.apr_bonus": "Bonus APR:",
    "sub.deposit_limit": "Límite de depósito instantáneo",
    "sub.contact_eyebrow": "¿Necesitas mejorar?",
    "sub.contact_title": "Contacta al gestor de tu cuenta",
    "sub.contact_body": "Contacta al gestor de tu cuenta para activar el nivel deseado.",
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem("orbital_lang") || "en";
  });

  const setLang = useCallback((l) => {
    if (!dict[l]) return;
    localStorage.setItem("orbital_lang", l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (key, vars) => {
      let s = dict[lang]?.[key] ?? dict.en[key] ?? key;
      if (vars && typeof s === "string") {
        Object.keys(vars).forEach((k) => {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]);
        });
      }
      return s;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "es", label: "ES", name: "Español" },
];

